import React, { useEffect, useState } from 'react'
import {
  Button,
  Card,
  Empty,
  Input,
  Rate,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd'
import PageTitle from 'components/PageTitle'
import PaginationContainer from '../../components/Forms/Pagination'
import {
  formatUrl,
  getRequest,
  growthDecideUrl,
  growthOpportunitiesUrl,
  postRequest
} from '../../config'
import OpportunityDrawer from './OpportunityDrawer'
import RemindModal from './RemindModal'
import {
  KIND_LABELS,
  REACH_LABELS,
  STATUS_COLORS,
  STATUS_LABELS,
  buildOpportunityQuery,
  dateRange,
  deadlineState,
  flag
} from './utils'

const { Text } = Typography

const EVENT_KINDS = 'car_event,sourcing,diaspora'

// Everything the growth bot has found (spec: 0042_growth_bot Req 2). The same three choices as the
// Telegram buttons; a choice here counts the same.
const OpportunitiesPage = () => {
  const [tab, setTab] = useState('events')
  const [filters, setFilters] = useState({ kind: '', status: '', reach: '', startsWithin: 'any', search: '' })
  const [rows, setRows] = useState([])
  const [meta, setMeta] = useState({})
  const [loading, setLoading] = useState(false)
  const [drawer, setDrawer] = useState({ open: false, record: null })
  const [reminding, setReminding] = useState(null)

  const load = async (pageUrl = null) => {
    const query = buildOpportunityQuery({
      ...filters,
      kind: tab === 'partners' ? 'partner' : filters.kind || EVENT_KINDS
    })
    const response = await getRequest(
      pageUrl ? formatUrl(pageUrl) : `${growthOpportunitiesUrl}${query}`,
      'opportunities',
      setLoading
    )
    if (response.success) {
      setRows(response.data.results || [])
      setMeta({ ...response.data })
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line
  }, [tab, filters])

  const setFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }))

  const decide = async (record, action, days) => {
    const response = await postRequest(
      growthDecideUrl(record.id),
      { action, days },
      'choice',
      () => {}
    )
    if (response.success) {
      message.success(
        action === 'interested' ? 'Marked interested.' : action === 'dismissed' ? 'Hidden.' : `Back in ${days} days.`
      )
      load()
    } else {
      const detail = response.error?.response?.data?.detail
      if (detail) message.error(detail)
    }
  }

  /* eslint-disable react/display-name */
  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => setDrawer({ open: true, record: r })}>
            {r.name}
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {KIND_LABELS[r.kind] || r.kind}
            {r.source === 'team' ? ' · added by our team' : ''}
          </Text>
        </Space>
      )
    },
    ...(tab === 'events'
      ? [
          {
            title: 'When',
            key: 'when',
            render: (_, r) => dateRange(r.startsOn, r.endsOn)
          }
        ]
      : []),
    {
      title: 'Where',
      key: 'where',
      render: (_, r) => (
        <Space direction="vertical" size={2}>
          <span>
            {flag(r.countryCode)} {[r.city, r.countryName].filter(Boolean).join(', ')}
          </span>
          {REACH_LABELS[r.reach] ? <Tag color={REACH_LABELS[r.reach].color}>{REACH_LABELS[r.reach].label}</Tag> : null}
        </Space>
      )
    },
    {
      title: 'Fit',
      key: 'fit',
      render: (_, r) => (
        <Tooltip title={r.fitReason}>
          <span>
            <Rate disabled value={r.fitScore} style={{ fontSize: 12 }} />
          </span>
        </Tooltip>
      )
    },
    ...(tab === 'events'
      ? [
          {
            title: 'Deadline',
            key: 'deadline',
            render: (_, r) => {
              const state = deadlineState(r.deadlineOn)
              if (state === 'none') return <Text type="secondary">—</Text>
              return (
                <Tag color={state === 'soon' ? 'orange' : 'default'}>{dateRange(r.deadlineOn)}</Tag>
              )
            }
          }
        ]
      : []),
    {
      title: 'Status',
      key: 'status',
      render: (_, r) => <Tag color={STATUS_COLORS[r.status]}>{STATUS_LABELS[r.status] || r.status}</Tag>
    },
    {
      title: '',
      key: 'actions',
      render: (_, r) => (
        <Space wrap>
          <Button size="small" onClick={() => decide(r, 'interested')} disabled={r.status === 'interested'}>
            Interested
          </Button>
          <Button size="small" onClick={() => decide(r, 'dismissed')} disabled={r.status === 'dismissed'}>
            Not for us
          </Button>
          <Button size="small" onClick={() => setReminding(r)}>
            Remind me
          </Button>
          {r.url ? (
            <a href={r.url} target="_blank" rel="noopener noreferrer">
              Source
            </a>
          ) : null}
        </Space>
      )
    }
  ]
  /* eslint-enable react/display-name */

  return (
    <div className="container-fluid">
      <PageTitle title="Growth · Opportunities" />
      <Card
        title="Opportunities"
        extra={
          <Button type="primary" onClick={() => setDrawer({ open: true, record: null })}>
            Add opportunity
          </Button>
        }
      >
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            { key: 'events', label: 'Events' },
            { key: 'partners', label: 'Partner leads' }
          ]}
        />
        <Space wrap style={{ marginBottom: 16 }}>
          {tab === 'events' ? (
            <Select
              value={filters.kind}
              onChange={v => setFilter('kind', v)}
              style={{ width: 160 }}
              options={[
                { value: '', label: 'All kinds' },
                { value: 'car_event', label: 'Car events' },
                { value: 'sourcing', label: 'Sourcing fairs' },
                { value: 'diaspora', label: 'Diaspora' }
              ]}
            />
          ) : null}
          <Select
            value={filters.status}
            onChange={v => setFilter('status', v)}
            style={{ width: 170 }}
            options={[
              { value: '', label: 'Any status' },
              ...Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }))
            ]}
          />
          <Select
            value={filters.reach}
            onChange={v => setFilter('reach', v)}
            style={{ width: 150 }}
            options={[
              { value: '', label: 'Any reach' },
              { value: 'reachable', label: 'In reach' },
              { value: 'funded', label: 'Funded' },
              { value: 'out', label: 'Out of reach' }
            ]}
          />
          {tab === 'events' ? (
            <Select
              value={filters.startsWithin}
              onChange={v => setFilter('startsWithin', v)}
              style={{ width: 170 }}
              options={[
                { value: 'any', label: 'Any time' },
                { value: 30, label: 'Next 30 days' },
                { value: 90, label: 'Next 90 days' },
                { value: 180, label: 'Next 180 days' }
              ]}
            />
          ) : null}
          <Input.Search
            allowClear
            placeholder="Name or city"
            onSearch={v => setFilter('search', v)}
            style={{ width: 200 }}
          />
        </Space>
        {filters.reach === 'out' ? (
          <Typography.Paragraph type="secondary">
            Found but not sent: out of reach, and no funded route. Put the place in reach to get them.
          </Typography.Paragraph>
        ) : null}
        <Table
          rowKey="id"
          loading={loading}
          dataSource={rows}
          columns={columns}
          pagination={false}
          scroll={{ x: 900 }}
          locale={{ emptyText: <Empty description="Nothing found yet" /> }}
        />
        {rows.length ? <PaginationContainer meta={meta} getData={load} /> : null}
      </Card>

      <OpportunityDrawer
        open={drawer.open}
        record={drawer.record}
        partner={tab === 'partners'}
        onClose={() => setDrawer({ open: false, record: null })}
        onSaved={() => {
          setDrawer({ open: false, record: null })
          load()
        }}
      />
      <RemindModal
        record={reminding}
        onCancel={() => setReminding(null)}
        onConfirm={days => {
          const record = reminding
          setReminding(null)
          decide(record, 'snoozed', days)
        }}
      />
    </div>
  )
}

export default OpportunitiesPage
